import React from 'react';
import { useUserApiService } from 'Services/user';
import { useAppUser, appUserPathPrefixRemove } from './providers/AppUserProvider';
import { AppEnv } from 'App/utils';

interface WidgetConfig {
  id: string;
  type: string;
  async: boolean;
  attrs: Record<string, string>;
}

interface TenantWidgetSettings {
  enabled: boolean;
  webhookUrl: string;
  version: string;
}

interface WidgetDynamicParams {
  source: string;
  page: string;
  tenant: string;
}

const defaultSettings: TenantWidgetSettings = {
  enabled: false,
  webhookUrl: '',
  version: '',
};

const WIDGET_CONFIG: WidgetConfig = {
  id: 'emily-chat-widget-script',
  type: 'text/javascript',
  async: false,
  attrs: {
    'data-emil-source': 'test.emil.de',
    'data-emil-session': 'Emil Demo',
  },
};

const TENANT_SETTING_KEY = 'emily_chat_widget';
const EMILY_WIDGET_INIT_ONCE_FLAG = '__EMILY_WIDGET_INIT_ONCE__';

const cleanupEmilyChatWidget = (): void => {
  // Remove the script tag
  const existingScript = document.getElementById( WIDGET_CONFIG.id );
  if ( existingScript ) {
    existingScript.remove();
  }

  // Reset the initialization flag
  delete window[ EMILY_WIDGET_INIT_ONCE_FLAG ];

  // Clean up the Emily Chat Widget
  ( window as any )?.EmilChatWidget?.destroy?.();
};

const validateAndGetSettings = ( values: TenantWidgetSettings ): TenantWidgetSettings => {
  return {
    enabled: typeof values?.enabled === 'boolean' ? values?.enabled : false,
    webhookUrl: typeof values?.webhookUrl === 'string' ? values?.webhookUrl : '',
    version: typeof values?.version === 'string' ? values?.version : '',
  };
};

async function ensureWidgetInitialized(
  settings: TenantWidgetSettings,
  params: WidgetDynamicParams,
): Promise<void> {
  if ( !settings.enabled || settings.webhookUrl === '' || settings.version === '' ) {
    window[ EMILY_WIDGET_INIT_ONCE_FLAG ] = true;
    return;
  };

  const existing = document.getElementById( WIDGET_CONFIG.id ) as HTMLScriptElement | null;
  if ( existing ) {
    window[ EMILY_WIDGET_INIT_ONCE_FLAG ] = true;
    return;
  }

  // Build widget src and preflight check to avoid executing HTML responses if the widget is not available
  const src = `${window.location.origin}/demo_emily/${settings.version}/widget.js`;
  try {
    const resp = await fetch( src, { method: 'GET', mode: 'cors' } );
    const ct = resp.headers.get( 'content-type' ) || '';
    if ( !resp.ok || !/javascript|ecmascript/i.test( ct ) ) return;
  } catch {
    return;
  }

  const script: HTMLScriptElement = document.createElement( 'script' );
  script.id = WIDGET_CONFIG.id;
  script.type = WIDGET_CONFIG.type;
  script.async = WIDGET_CONFIG.async;
  Object.entries( WIDGET_CONFIG.attrs ).forEach( ( [ key, value ] ) => {
    script.setAttribute( key, value );
  } );
  script.src = src;

  script.onload = () => {
    ( window as any )?.EmilChatWidget?.init( {
      webhookUrl: settings.webhookUrl,
      customFields: {
        ...params,
      },
    } );

    window[ EMILY_WIDGET_INIT_ONCE_FLAG ] = true;
  };

  script.onerror = () => { /* silent */ };

  document.head.append( script );
}

// Export the cleanup function and types for use in other components
export { cleanupEmilyChatWidget };
export type { WidgetDynamicParams };

export const EmilyChatWidgetLoader: React.FC = () => {
  const tenantService = useUserApiService();
  const user = useAppUser();

  React.useEffect( () => {
    if ( window[ EMILY_WIDGET_INIT_ONCE_FLAG ] ) return;

    const init = async () => {
      // Get dynamic parameters once
      const dynamicParams: WidgetDynamicParams = {
        source: process.env.REACT_APP_CONFIG || AppEnv.prod,
        page: appUserPathPrefixRemove( window.location.pathname ),
        tenant: user.tenantSlug || '',
      };

      try {
        const result = await tenantService.getTenantSettings( TENANT_SETTING_KEY );
        const values = result?.length > 0
          ? ( result[0] as unknown as TenantWidgetSettings )
          : defaultSettings;

        const settings = validateAndGetSettings( values );

        await ensureWidgetInitialized( settings, dynamicParams );
      } catch {
        await ensureWidgetInitialized( defaultSettings, dynamicParams );
      }
    };

    // initialize the widget
    init();

    // Cleanup function to remove widget when component unmounts
    return () => {
      cleanupEmilyChatWidget();
    };
  }, [ tenantService, user.tenantSlug ] );

  return null;
};
